import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "../../../../services/abstract.service";
import { OrderAttributesExt, ShipmentRequestInterface } from "./interfaces/create-order.interface";
import { IAPIItemResult, IAPIListResult, IPagination, IPaginationResult, IPaginationResultFrequencies } from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { andAllWhere, andWhere, buildQuery, CombinedFilter, Ops, orderBy, OrderVariant, where, noop, take, select, orWhere, page } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { CompanyAttributes, DocumentClassAttributes, ExpeditionConditionAttributes, MastersUserAttributes, MaterialAttributes, MaterialStockAttributes, OrderAttributes, SubRouteAttributes, ViewMaterialStockAttributes } from "@albatrosdeveloper/ave-models-package";

import RouteAttributes from "@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import SalesOfficeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity";
import { OrderMaterialRequestFields } from "./interfaces/fetch-sequence-materials-fields-request.interface"
import CurrencyAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/currency/currency.entity";
import PaymentConditionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity";
import { MaterialsUMSelectionInterface } from "./interfaces/materials-um-selection-params.interface";
import { ShipToInterface } from "./interfaces/fetch-ship-to.interface";
import MaterialGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-group/material-group.entity";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { MaterialListInterface } from "./interfaces/material-list.interface";

const routingUrl = env.API_ROUTING_URL + 'api/';
const materialUrl = env.API_MATERIAL_URL + 'api/';
const businessPartnerUrl = env.API_BUSINESS_PARTNER + 'api/';
const orderUrl = env.API_AVE_ORDER_URL + 'api/';
const configurationUrl = env.API_CONFIGURATION_URL + 'api/';
const documentUrl = env.API_DOCUMENT_URL + 'api/';
const masterUrl = env.API_UNIT_MEASURE + 'api/';
const priceListUrl = env.API_MATERIAL_PRICE_LIST_URL + 'api/';
const moduleName = 'shipments-preparation/';
const authorizationURL = env.API_AVE_AUTHORIZATION_URL + 'api/';

@Injectable({
  providedIn: "root",
})
export class OrdersCreationService extends AbstractService<OrderAttributes,Partial<OrderAttributes>,Partial<OrderAttributes>> {
  constructor(
    http: HttpClient,
  ) {
    super(orderUrl, moduleName, http);
  }

  /**
   * List
   * @param filter CombinedFilter of related entity - RoutesAttributes
   * @returns Data list
   */
  fetchRoutes(
    filter?: CombinedFilter<RouteAttributes>,
  ): Observable<IAPIListResult<RouteAttributes>> {
    let API_URL: string = routingUrl + 'route/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<RouteAttributes>>(API_URL);
  }

  /**
   * List
   * @param filter CombinedFilter of related entity - SubRoutesAttributes
   * @returns Data list
   */
  fetchSubRoutes(
    filter?: CombinedFilter<SubRouteAttributes>,
  ): Observable<IAPIListResult<SubRouteAttributes>> {
    let API_URL: string = routingUrl + 'sub-route/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SubRouteAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - BusinessPartnerAttributes
    * @returns Data list
  */
  fetchBusinessPartners(
    filter?: CombinedFilter<BusinessPartnerAttributes>,
  ): Observable<IAPIListResult<BusinessPartnerAttributes>> {
    let API_URL: string = businessPartnerUrl + 'business-partner/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<BusinessPartnerAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - MastersUserAttributes
    * @returns Data list
  */
  fetchMasterUsers(
    filter?: CombinedFilter<MastersUserAttributes>,
  ): Observable<IAPIListResult<any>> {
    let API_URL: string = masterUrl + 'master_user/user';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<any>>(API_URL);
  }

  findPreSaleAllUserLogin(data){
    let API_URL: string = masterUrl + 'master_user/presale/user';
    return this.http.post<IAPIListResult<any>>(API_URL, data);
  }

  /**
    * Item
    * @param id String for _id of related entity - SalesOfficeAttributes
    * @returns Data Item
  */
  fetchSalesOfficeById(
    id: string,
  ): Observable<IAPIItemResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/byId/${id}`;
    return this.http.get<IAPIItemResult<SalesOfficeAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - SalesOfficeAttributes
    * @returns Data list
  */
  fetchSalesOffices(
    filter?: CombinedFilter<SalesOfficeAttributes>,
  ): Observable<IAPIListResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SalesOfficeAttributes>>(API_URL);
  }

  fetchSalesOfficesByDocumentClass(documentClassId: string): Observable<IAPIListResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/byDocumentClass/${documentClassId}`;
    return this.http.get<IAPIListResult<SalesOfficeAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - DocumentClassAttributes
    * @returns Data list
  */
  fetchDocumentClasses(
    filter?: CombinedFilter<DocumentClassAttributes>,
  ): Observable<IAPIListResult<DocumentClassAttributes>> {
    let API_URL: string = `${documentUrl}document_class/filter`;
    if (filter) {
        API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<DocumentClassAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - ExpeditionConditionAttributes
    * @returns Data list
  */
  fetchExpeditionCondition(
    filter?: CombinedFilter<ExpeditionConditionAttributes>,
  ): Observable<IAPIListResult<ExpeditionConditionAttributes>> {
    let API_URL: string = `${masterUrl}expedition_condition/filter`;
    if (filter) {
        API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<ExpeditionConditionAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - CurrencyAttributes
    * @returns Data list
  */
  fetchCurrencies(
    filter?: CombinedFilter<CurrencyAttributes>,
  ): Observable<IAPIListResult<CurrencyAttributes>> {
    let API_URL: string = `${masterUrl}currency/filter`;
    if (filter) {
        API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<CurrencyAttributes>>(API_URL);
  }

  /**
    * Pagination
    * @param filter CombinedFilter of related entity - MaterialAttributes
    * @returns Paginated data
  */
  fetchMaterialsWithCount(
    filter?: CombinedFilter<MaterialAttributes>,
    fieldsData?: OrderMaterialRequestFields,
    materialsUMSelection?: MaterialsUMSelectionInterface[]
  ): Observable<IPaginationResult<MaterialAttributes>> {
    let API_URL: string = materialUrl + 'material/sequence-pricing';
    let sequencePricingReq: {
        query: CombinedFilter<MaterialAttributes>,
        fieldsData?: OrderMaterialRequestFields,
        materialsUMSelection?: MaterialsUMSelectionInterface[]
    } = { query: filter };
    if (fieldsData) sequencePricingReq.fieldsData = fieldsData;
    if (materialsUMSelection && materialsUMSelection.length) sequencePricingReq.materialsUMSelection = materialsUMSelection;
    return this.http.post<IPaginationResult<MaterialAttributes>>(API_URL, sequencePricingReq);
  }

  /**
    * @param ordersRequest Orders request to validate and request
    * @returns TODO
  */
  validateOrderRequest(
    ordersRequest: ShipmentRequestInterface,
  ): Observable<any> {
    let API_URL: string = orderUrl + moduleName + 'validate-and-create';
    return this.http.post<any>(API_URL, ordersRequest);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - PaymentConditionAttributes
    * @returns Data list
  */
  fetchPaymentConditions(
    filter?: CombinedFilter<PaymentConditionAttributes>,
  ): Observable<IAPIListResult<PaymentConditionAttributes>> {
    let API_URL: string = `${masterUrl}payment_condition/filter`;
    if (filter) {
        API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<PaymentConditionAttributes>>(API_URL);
  }

  /**
    * Item
    * @param id String for _id of related entity - CompanyAttributes
    * @returns Data Item
  */
  fetchCompanyById(
    id: string,
  ): Observable<IAPIItemResult<CompanyAttributes>> {
    let API_URL: string = `${configurationUrl}company/byId/${id}`;
    return this.http.get<IAPIItemResult<CompanyAttributes>>(API_URL);
  }

  fetchShipTo(shipToRequest:ShipToInterface): Observable<any> { //TODO RM
    let API_URL: string = `${businessPartnerUrl}business-partner/get_shiptos_with_soldtos_by_query`;
    return this.http.post<IAPIItemResult<any>>(API_URL, shipToRequest); //TODO RM
  } 

  fetchPrice(dataRequest): Observable<any>{ //TODO M
    let API_URL: string = `${priceListUrl}sequence-reading/pricing`;
    return this.http.post<any>(API_URL, dataRequest); //TODO M
  }

  fetchApplications(applicationId:string, subApplicationId:string): Observable<any> {
    let API_URL: string = `${authorizationURL}applications/get_app?applicationId=${applicationId}&subApplicationId=${subApplicationId}`;
    return this.http.get<IAPIItemResult<any>>(API_URL);
  }

  getApplications(): Observable<any> {
    let API_URL: string = `${authorizationURL}applications`;
    return this.http.get<IAPIItemResult<any>>(API_URL);
  }

  fetchCurrencyFactor(currencySourceId:string, currencyTargetId:string): Observable<any> {
    let API_URL: string = `${masterUrl}currency/get_factor?currencySourceId=${currencySourceId}&currencyTargetId=${currencyTargetId}`;
    return this.http.get<IAPIItemResult<any>>(API_URL);
  }

  generateOrder(dataRequest:ShipmentRequestInterface):Observable<any>{ //TODO crear tipo de dato
    let API_URL: string = `${orderUrl}shipments-preparation/validate-and-create`;
    return this.http.post<any>(API_URL, dataRequest);
  }

  fetchStock(dataRequest): Observable<IPaginationResult<MaterialStockAttributes>> {
    const extraFilters: any[] = [];
    if (dataRequest.unitMeasureIds && dataRequest.unitMeasureIds.length > 0)
      extraFilters.push(andWhere('unitOfMeasureId', Ops.in(...dataRequest.unitMeasureIds)));
    if (dataRequest.warehouseId)
      extraFilters.push(andWhere('warehouseId', Ops.eq(dataRequest.warehouseId)));
    if (dataRequest.warehouseIds && dataRequest.warehouseIds.length > 0)
      extraFilters.push(andWhere('warehouseId', Ops.in(...dataRequest.warehouseIds)));

    const query = buildQuery<MaterialStockAttributes>(
      where('materialId', Ops.in(...dataRequest.materialsIds)),
      ...extraFilters,
      andWhere("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
    )

    let API_URL: string = `${materialUrl}material_stock/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialStockAttributes>>(API_URL);
  }

  fetchCategories(companyId: number, divisionIds: number[], categoryCode: string): Observable<IAPIListResult<MaterialGroupAttributes>> {
    const query = buildQuery<MaterialGroupAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      andWhere(<any>"company.id", Ops.in(companyId)),
      divisionIds && divisionIds.length > 0 ? andWhere(<any>'division.id', Ops.in(...divisionIds)) : noop(),
      categoryCode == '0' ? noop() : orWhere(<any>'hierarchy.code', Ops.eq(categoryCode)),
      select(["_id", "code", "name", "hasSubcategories", "fatherCode"]),
      orderBy("name", OrderVariant.Asc),
    )

    let API_URL: string = `${materialUrl}material_group/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IAPIListResult<MaterialGroupAttributes>>(API_URL)
  }

  findAllMaterialsWithCount(
    options: IPagination<MaterialListInterface>, 
    data: any,
  ): Observable<IPaginationResultFrequencies<MaterialListInterface>> {
    let dataToSend = {
      search: options.search,
      categoryCode: data.categoryCode,
    }
    const query = buildQuery<MaterialListInterface>(
      where("name", Ops.like(options.search ?? "", "i")),
      andWhere("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      data.companyIds && data.companyIds.length > 0 ? andWhere('company.id' as keyof MaterialListInterface, Ops.in(...data.companyIds)) : noop(),
      data.divisionIds && data.divisionIds.length > 0 ? andWhere('division.id' as keyof MaterialListInterface, Ops.in(...data.divisionIds)) : noop(),
      data.distributionChainId ? andWhere(<any>'distributionChains.id', Ops.eq(data.distributionChainId)) : noop(),
      data.materialsToFilter.length > 0 ? andWhere('code', Ops.in(...data.materialsToFilter)) : noop(),
      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      materialUrl +
      `${'material'}/withCountV2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.post<IPaginationResultFrequencies<MaterialAttributes>>(API_URL, dataToSend);
  }

  getOrderById(orderId): Observable<IAPIItemResult<OrderAttributes>> {
    const API_URL: string = orderUrl + `order/byId/${orderId}`;
    return this.http.get<IAPIItemResult<OrderAttributes>>(API_URL)
  }

  findAllMaterialsAndStockWithCount(
    options: IPagination<MaterialListInterface>, 
    data: any,
    dataStock: any,
  ): Observable<IPaginationResultFrequencies<MaterialListInterface>> {
    let dataToSend = {
      search: options.search,
      categoryCode: data.categoryCode,
      warehouseId: dataStock.warehouseId ? dataStock.warehouseId : null,
      withStock: dataStock.withStock ? true : null,
    }
    const query = buildQuery<MaterialListInterface>(
      //where("name", Ops.like(options.search ?? "", "i")),
      where("statusId", StatusEnum.ACTIVE),
      andWhere("_deleted", Ops.eq(false)),
      data.divisionIds && data.divisionIds.length > 0 ? andWhere('divisionId', Ops.in(...data.divisionIds)) : noop(),
      data.distributionChainId ? andWhere('distributionChainIds', Ops.eq(data.distributionChainId)) : noop(),
      data.materialsToFilter.length > 0 ? andWhere('code', Ops.in(...data.materialsToFilter)) : noop(),
      //data.withStock ? andWhere(<any>'materialStock.availableStock', Ops.gt(0)) : noop(),
      //dataStock.warehouseId ? andWhere(<any>'materialStock.warehouseId', Ops.eq(dataStock.warehouseId)): noop(),
      orderBy(
        options.sort ?? "position",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc,
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      materialUrl +
      `${'view_material_stock'}/withCountV2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.post<IPaginationResultFrequencies<MaterialListInterface>>(API_URL, dataToSend);
  }
}
